import { defineStore } from "pinia";
import { ref } from "vue";
import useMyFetch from "@stores/useMyFetch";
import { useDirectUpload } from "@hooks/useDirectUpload";
import { getClientId } from "@utils/clientId";
import type { FormatValue } from "@schemas/surveyDataImport";
import { captureEvent } from "@/lib/analytics";

type ImportStatus = "idle" | "uploading" | "validating" | "valid" | "invalid" | "submitting";

interface ValidationResult {
  valid: boolean;
  errors?: string[];
  signed_blob_id?: string;
  respondent_count?: number;
  question_count?: number;
}

interface DirectUploadBlob {
  signed_id: string;
}

interface CreateSurveyParams {
  name: string;
  language: string;
  accountId: string | number;
  format: FormatValue;
  contractId: number | null;
}

export const useSurveyDataImportStore = defineStore("surveyDataImport", () => {
  const status = ref<ImportStatus>("idle");
  const result = ref<ValidationResult | null>(null);
  const signedBlobId = ref<string | null>(null);
  const submitError = ref("");

  const reset = () => {
    status.value = "idle";
    result.value = null;
    signedBlobId.value = null;
    submitError.value = "";
  };

  const uploadToActiveStorage = (file: File): Promise<DirectUploadBlob> =>
    new Promise((resolve, reject) => {
      const { uploadFile } = useDirectUpload(
        (blob: DirectUploadBlob) => resolve(blob),
        (error: unknown) => reject(error),
      );
      uploadFile(file);
    });

  const validate = async (file: File | null, format: FormatValue) => {
    if (!file) {
      reset();
      return;
    }

    status.value = "uploading";
    result.value = null;
    signedBlobId.value = null;

    try {
      const blob = await uploadToActiveStorage(file);
      signedBlobId.value = blob.signed_id;
      status.value = "validating";

      const { response, data } = await useMyFetch("/survey_data_imports/validate")
        .post({ signed_blob_id: blob.signed_id, format_type: format })
        .json<ValidationResult>();

      if (response.value?.ok && data.value) {
        result.value = data.value;
        status.value = data.value.valid ? "valid" : "invalid";
      } else {
        result.value = { valid: false, errors: ["Validation request failed. Please try again."] };
        status.value = "invalid";
      }
    } catch (err) {
      result.value = { valid: false, errors: [(err as Error)?.message || "Upload failed. Please try again."] };
      status.value = "invalid";
    }
  };

  const createSurvey = async ({ name, language, accountId, format, contractId }: CreateSurveyParams): Promise<boolean> => {
    if (status.value !== "valid" || !signedBlobId.value) return false;

    status.value = "submitting";
    submitError.value = "";

    try {
      const { response } = await useMyFetch("/survey_data_imports")
        .post({
          signed_blob_id: signedBlobId.value,
          format_type: format,
          name,
          language,
          account_id: accountId,
          contract_id: contractId,
          client_id: getClientId(),
        })
        .json();

      if (response.value?.ok) {
        signedBlobId.value = null;
        captureEvent("survey_data_import_started", { format_type: format });
        return true;
      }

      submitError.value = "Failed to start the import. Please try again.";
      status.value = "valid";
      return false;
    } catch (err) {
      submitError.value = (err as Error)?.message || "Failed to start the import. Please try again.";
      status.value = "valid";
      return false;
    }
  };

  const discard = async () => {
    const blobId = signedBlobId.value;
    reset();
    if (!blobId) return;

    try {
      await useMyFetch("/survey_data_imports/discard").post({ signed_blob_id: blobId }).json();
    } catch {
      // Best-effort: dialog has already closed, so there's nowhere to surface a failure.
    }
  };

  return {
    status,
    result,
    signedBlobId,
    submitError,
    reset,
    validate,
    createSurvey,
    discard,
  };
});
